
import React, { useState } from 'react';
import { Loader2, Search, X, ArrowLeft, Check, Info, Tag, FileText, Database } from 'lucide-react';

interface LoincSearchModalProps {
  onClose: () => void;
  onSelect: (loinc: any) => void;
}

const LoincSearchModal: React.FC<LoincSearchModalProps> = ({ onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  
  // État pour la vue détaillée
  const [selectedDetail, setSelectedDetail] = useState<any | null>(null);

  // IMPORTANT: Remplacez cette URL par l'URL de production de votre webhook n8n.
  const N8N_WEBHOOK_URL = 'https://n8n.mhasimbola.com/webhook/129e59cb-8ad6-478b-a90f-951047402f97';

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSearched(true);
    setSelectedDetail(null); // Reset detail view on new search

    if (N8N_WEBHOOK_URL.includes('VOTRE_URL_N8N')) {
        setError("Erreur de configuration : Veuillez remplacer l'URL du webhook n8n dans le code source (components/admin/LoincSearchModal.tsx).");
        setLoading(false);
        return;
    }

    try {
      const response = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: searchTerm,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Erreur du serveur n8n: ${response.statusText}`);
      }

      const data = await response.json();
      
      let resultsArray = [];
      // Cas 1 : La réponse est la structure complète [ { ..., Results: [...] } ]
      if (Array.isArray(data) && data.length > 0 && data[0].Results) {
          resultsArray = data[0].Results;
      // Cas 2 : La réponse est directement le tableau de résultats [...]
      } else if (Array.isArray(data)) {
          resultsArray = data;
      }

      setResults(resultsArray);
      
    } catch (err: any) {
      setError(`Impossible de contacter votre serveur n8n. ${err.message}`);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSelection = () => {
      if (selectedDetail) {
          onSelect(selectedDetail);
      }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl overflow-hidden max-h-[85vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 shrink-0">
          <div className="flex items-center gap-3">
              {selectedDetail && (
                  <button 
                    onClick={() => setSelectedDetail(null)} 
                    className="p-1 rounded hover:bg-slate-200 text-slate-500 transition-colors"
                    title="Retour aux résultats"
                  >
                      <ArrowLeft size={20} />
                  </button>
              )}
              <h3 className="font-bold text-lg text-slate-800">
                  {selectedDetail ? 'Détails du code LOINC' : 'Recherche LOINC via n8n'}
              </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex flex-col">
            {selectedDetail ? (
                // --- VUE DÉTAILLÉE ---
                <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-white">
                    <div>
                        <div className="flex justify-between items-start gap-4 mb-2">
                            <h2 className="text-xl font-bold text-slate-900 leading-tight">
                                {selectedDetail.LONG_COMMON_NAME}
                            </h2>
                            <span className="font-mono text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded border border-blue-200 whitespace-nowrap">
                                {selectedDetail.LOINC_NUM}
                            </span>
                        </div>
                        <p className="text-slate-500 text-sm">
                            {selectedDetail.SHORTNAME}
                        </p>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-100">
                        <div>
                            <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Composant</span>
                            <span className="text-sm font-medium text-slate-800 break-words">{selectedDetail.COMPONENT || '-'}</span>
                        </div>
                        <div>
                            <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Système</span>
                            <span className="text-sm font-medium text-slate-800">{selectedDetail.SYSTEM || '-'}</span>
                        </div>
                        <div>
                            <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Classe</span>
                            <span className="text-sm font-medium text-slate-800">{selectedDetail.CLASS || '-'}</span>
                        </div>
                        <div>
                            <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Unité (Ex.)</span>
                            <span className="text-sm font-medium text-slate-800">{selectedDetail.EXAMPLE_UCUM_UNITS || selectedDetail.EXAMPLE_UNITS || '-'}</span>
                        </div>
                    </div>

                    {/* Descriptions & Definitions */}
                    {(selectedDetail.DefinitionDescription || (selectedDetail.TermDescriptions && selectedDetail.TermDescriptions.length > 0)) && (
                        <div className="space-y-4">
                            <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2 border-b border-slate-100 pb-2">
                                <FileText size={16} className="text-indigo-500"/> Description & Définition
                            </h4>
                            
                            {selectedDetail.DefinitionDescription && (
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 mb-1 uppercase">Définition</p>
                                    <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 p-3 rounded border border-slate-100 text-justify">
                                        {selectedDetail.DefinitionDescription}
                                    </p>
                                </div>
                            )}

                            {selectedDetail.TermDescriptions && selectedDetail.TermDescriptions.map((desc: any, idx: number) => (
                                <div key={idx}>
                                    <p className="text-xs font-semibold text-slate-500 mb-1 uppercase">Description additionnelle {idx + 1}</p>
                                    <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-3 rounded border border-slate-100 text-justify">
                                        {desc.Description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Related Names (Synonyms) */}
                    {selectedDetail.RELATEDNAMES2 && (
                        <div>
                            <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2 border-b border-slate-100 pb-2 mb-3">
                                <Tag size={16} className="text-indigo-500"/> Mots-clés / Synonymes
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {selectedDetail.RELATEDNAMES2.split(';').map((tag: string, i: number) => (
                                    <span key={i} className="px-2.5 py-1 bg-white border border-slate-200 text-slate-600 text-xs rounded-full shadow-sm hover:bg-slate-50 hover:border-indigo-200 transition-colors">
                                        {tag.trim()}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                // --- VUE LISTE ---
                <>
                    <div className="p-4 border-b border-slate-200 shrink-0 bg-slate-50/50">
                        <form onSubmit={handleSearch} className="flex flex-col gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Ex: glucose, creatinine..."
                                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    autoFocus
                                />
                            </div>
                            
                            <div className="flex justify-between items-center">
                                {searched && !loading && (
                                    <span className="text-xs text-slate-500 font-medium px-1">
                                        {results.length} résultats trouvés
                                    </span>
                                )}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="ml-auto px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                                >
                                {loading ? <Loader2 size={16} className="animate-spin" /> : 'Rechercher'}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="overflow-y-auto flex-1 bg-slate-50">
                        {loading && (
                            <div className="p-12 text-center text-slate-500 flex flex-col items-center gap-3">
                                <Loader2 size={32} className="animate-spin text-indigo-400" />
                                <p>Recherche en cours...</p>
                            </div>
                        )}
                        {error && (
                            <div className="p-8 text-center text-red-600 bg-red-50 mx-4 mt-4 rounded-lg border border-red-100">{error}</div>
                        )}
                        {!loading && !error && (
                            <div className="p-4">
                            {results.length > 0 ? (
                                <ul className="space-y-3">
                                {results.map((loinc) => {
                                    // Extract a description snippet for the list view
                                    const descriptionSnippet = loinc.DefinitionDescription || loinc.TermDescriptions?.[0]?.Description;
                                    
                                    return (
                                        <li
                                            key={loinc.LOINC_NUM}
                                            onClick={() => setSelectedDetail(loinc)}
                                            className="bg-white p-4 rounded-lg border border-slate-200 hover:border-indigo-300 hover:shadow-md cursor-pointer transition-all group"
                                        >
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex-1">
                                                    <p className="font-bold text-slate-800 text-sm group-hover:text-indigo-700 transition-colors">
                                                        {loinc.LONG_COMMON_NAME}
                                                    </p>
                                                    
                                                    {/* Attributes Row */}
                                                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
                                                        <span className="flex items-center gap-1"><Database size={10}/> {loinc.SYSTEM}</span>
                                                        {loinc.EXAMPLE_UNITS && <span className="font-medium bg-slate-100 px-1.5 rounded text-slate-600">{loinc.EXAMPLE_UNITS}</span>}
                                                    </div>

                                                    {/* Description Snippet */}
                                                    {descriptionSnippet && (
                                                        <p className="text-xs text-slate-400 mt-2 line-clamp-2 leading-snug">
                                                            {descriptionSnippet}
                                                        </p>
                                                    )}

                                                    {/* Synonyms (RELATEDNAMES2) - Added */}
                                                    {loinc.RELATEDNAMES2 && (
                                                        <p className="text-[10px] text-slate-400 mt-1 italic truncate">
                                                            <Tag size={10} className="inline mr-1"/>
                                                            {loinc.RELATEDNAMES2.split(';').join(', ')}
                                                        </p>
                                                    )}
                                                </div>
                                                <span className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded border border-slate-200 group-hover:bg-indigo-50 group-hover:text-indigo-700 group-hover:border-indigo-100 transition-colors shrink-0">
                                                    {loinc.LOINC_NUM}
                                                </span>
                                            </div>
                                        </li>
                                    );
                                })}
                                </ul>
                            ) : (
                                searched && (
                                    <div className="p-12 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
                                        Aucun résultat trouvé pour "{searchTerm}".
                                    </div>
                                )
                            )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>

        {/* Footer (Only shown in Detail View) */}
        {selectedDetail && (
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 shrink-0 animate-in slide-in-from-bottom-2">
                <button 
                    onClick={() => setSelectedDetail(null)}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors"
                >
                    Retour à la liste
                </button>
                <button 
                    onClick={handleConfirmSelection}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-colors flex items-center gap-2 shadow-sm"
                >
                    <Check size={18} /> Importer ce code
                </button>
            </div>
        )}
      </div>
    </div>
  );
};

export default LoincSearchModal;
